import { useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useToastContext } from "../../contexts/ToastContext/ToastContext";
import useUserType from "../../hooks/useUserType";
import localeText from "../../locale";
import ShareService from "../../service/ShareService";
import {
  buildShareLink,
  canEarnShareCommission,
  formatPercent,
  getShareCommissionPercent,
} from "./promoterShare";

const shareService = new ShareService();

// Página do produto: o Promoter gera o próprio link e ganha a comissão definida pelo vendedor.
export default function PromoterShareBox({ product }) {
  const { userType } = useUserType();
  const { showInfoToast, showErrorToast } = useToastContext();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!canEarnShareCommission(userType, product)) return null;

  const percent = formatPercent(getShareCommissionPercent(product));

  const generateLink = async () => {
    setLoading(true);
    try {
      const shareToken = await shareService.getOrCreateShareToken(
        product.id,
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
      );
      if (!shareToken) throw new Error("no share token");
      setLink(buildShareLink(product.id, shareToken));
    } catch (e) {
      showErrorToast({ detail: "promoterLinkError" });
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      showInfoToast({ summary: "result", detail: "linkCopied" });
    } catch (e) {
      showErrorToast({ detail: "promoterLinkError" });
    }
  };

  const shareLink = async () => {
    try {
      await navigator.share({ title: product.title, url: link });
    } catch (e) {
      if (e?.name !== "AbortError") copyLink();
    }
  };

  return (
    <div
      className="flex flex-column gap-2 border-round-lg p-3 mt-3"
      style={{ backgroundColor: "#ECFDF5", border: "1px solid #A7F3D0" }}
    >
      <div className="flex align-items-center gap-2 font-semibold" style={{ color: "#047857" }}>
        <i className="pi pi-share-alt" />
        {localeText("promoterShareTitle", { percent })}
      </div>
      <div className="text-sm" style={{ color: "#344563" }}>
        {localeText("promoterShareDescription", { percent })}
      </div>

      {!link ? (
        <Button
          type="button"
          icon="pi pi-link"
          label={localeText("promoterGenerateLink")}
          loading={loading}
          onClick={generateLink}
          className="p-button-sm p-button-success align-self-start"
        />
      ) : (
        <div className="flex gap-2 align-items-center">
          <InputText
            value={link}
            readOnly
            onFocus={(e) => e.target.select()}
            className="p-inputtext-sm flex-1"
            style={{ minWidth: 0 }}
            aria-label={localeText("promoterGenerateLink")}
          />
          <Button
            type="button"
            icon="pi pi-copy"
            onClick={copyLink}
            className="p-button-sm p-button-success"
            tooltip={localeText("promoterCopyLink")}
            aria-label={localeText("promoterCopyLink")}
          />
          {"share" in navigator && (
            <Button
              type="button"
              icon="pi pi-share-alt"
              onClick={shareLink}
              className="p-button-sm p-button-outlined p-button-success"
              aria-label={localeText("promoterShareAction")}
            />
          )}
        </div>
      )}
    </div>
  );
}
